import { Box, Typography } from '@mui/material'
import { FC } from 'react'
import { tokens } from '@/theme'

export interface AnelProgressoProps {
  /** 0–100; fora da faixa é truncado (o anel nunca passa da volta inteira). */
  percentual: number
  /** Texto de acessibilidade ("Meta do trimestre: 42%"). */
  rotulo: string
  /** Linhas ao lado do anel (rótulo + valor), ex.: fechado / meta / faltam. */
  linhas?: { rotulo: string; valor: string; destaque?: boolean }[]
  tamanho?: number
}

/**
 * Anel de progresso (conceito BI, 11/09): trilha cinza, arco no laranja Originei e o
 * percentual em Ink no centro. SVG puro — sem lib de gráfico.
 */
export const AnelProgresso: FC<AnelProgressoProps> = ({
  percentual,
  rotulo,
  linhas = [],
  tamanho = 136,
}) => {
  const pct = Math.max(0, Math.min(100, Number.isFinite(percentual) ? percentual : 0))
  const espessura = 12
  const raio = (tamanho - espessura) / 2
  const circunferencia = 2 * Math.PI * raio
  const preenchido = (pct / 100) * circunferencia
  const centro = tamanho / 2
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
      <Box
        role="img"
        aria-label={`${rotulo}: ${Math.round(pct)}%`}
        sx={{ position: 'relative', width: tamanho, height: tamanho, flexShrink: 0 }}
      >
        <svg width={tamanho} height={tamanho} viewBox={`0 0 ${tamanho} ${tamanho}`}>
          <circle
            cx={centro}
            cy={centro}
            r={raio}
            fill="none"
            stroke={tokens.brandPrimarySoft}
            strokeWidth={espessura}
          />
          <circle
            cx={centro}
            cy={centro}
            r={raio}
            fill="none"
            stroke={tokens.brandPrimary}
            strokeWidth={espessura}
            strokeLinecap="round"
            strokeDasharray={`${preenchido} ${circunferencia - preenchido}`}
            transform={`rotate(-90 ${centro} ${centro})`}
            style={{ transition: 'stroke-dasharray 300ms' }}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            component="div"
            data-testid="anel-percentual"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: tokens.brandInk,
            }}
          >
            {Math.round(pct)}%
          </Typography>
        </Box>
      </Box>
      {linhas.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
          {linhas.map((l) => (
            <Box key={l.rotulo}>
              <Typography
                variant="caption"
                component="div"
                sx={{ color: tokens.textSecondary, lineHeight: 1.2 }}
              >
                {l.rotulo}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontWeight: l.destaque ? 700 : 600,
                  fontSize: l.destaque ? '1rem' : '0.875rem',
                  color: l.destaque ? tokens.brandPrimaryDark : tokens.brandInk,
                }}
              >
                {l.valor}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default AnelProgresso
